import { addMinutes, getDay } from "date-fns"
import { create } from "zustand"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"
import useCalendarConfigStore from "@/modules/calendar/stores/use-calendar-config"
import roundTime from "@/modules/calendar/utils/round-time"
import formatToISODateTime from "@/modules/calendar/utils/format-to-ISO-date-time"

type OpenSideSheetForAdditionParams = {
  type: "addition"
}

type OpenSideSheetForEditionParams = {
  type: "edition"
}

type OpenSideSheetForDetailsParams = {
  type: "details"
  event: UserEvent
}

type OpenSideSheetParams =
  OpenSideSheetForEditionParams | OpenSideSheetForAdditionParams | OpenSideSheetForDetailsParams

interface UserEventSideSheetState {
  isOpen: boolean
  type: "addition" | "edition" | "details"
  event: UserEvent
  isAddition: boolean
  isDetails: boolean
  isEdition: boolean
}

interface UserEventSideSheetActions {
  openSideSheet: (params: OpenSideSheetParams) => void
  closeSideSheet: () => void
}

const now = new Date()
const step = useCalendarConfigStore.getState().step
const startDate = roundTime(formatToISODateTime(now), step)
const endDate = addMinutes(new Date(startDate), step).toISOString()

const initialState: UserEventSideSheetState = {
  isOpen: false,
  type: "addition",
  event: {
    id: "",
    startDate,
    endDate,
    day: getDay(now),
    parent: {
      id: "",
      createdBy: "user",
      title: "",
      days: [],
      color: "",
      category: "study",
    },
  },
  isAddition: true,
  isDetails: false,
  isEdition: false,
}

const useUserEventSideSheet = create<UserEventSideSheetState & UserEventSideSheetActions>()(
  (set) => ({
    ...initialState,
    openSideSheet: (params) => {
      const step = useCalendarConfigStore.getState().step

      if (params.type === "addition") {
        const now = new Date()
        const startDate = roundTime(formatToISODateTime(now), step)
        const endDate = addMinutes(new Date(startDate), step).toISOString()

        return set((state) => ({
          isOpen: true,
          type: params.type,
          event: { ...state.event, startDate, endDate },
          isAddition: true,
          isDetails: false,
          isEdition: false,
        }))
      }

      set({
        isOpen: true,
        type: params.type,
        ...(params.type === "details" && { event: params.event }),
        isAddition: false,
        isDetails: params.type === "details",
        isEdition: params.type === "edition",
      })
    },
    closeSideSheet: () => set(initialState),
  }),
)

export default useUserEventSideSheet
